import {
  FlexContainer,
  LinkButton,
  LoadingPage,
} from '@act/storybook';
import type { ReportCustomerDto, ReportDto } from '@act/shared/data-transfer-objects';
import { formatWarehouseMeasurementPeriodLabel } from '@act/shared/data-transfer-objects';
import {
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { reportsRequests } from 'redux/requests/reports.requests';
import { makeRequest } from 'redux/utils/apiRequest';
import { useApiCall } from 'redux/utils/apiHook.hook';
import { useToast } from 'react-components/app/ReusableToast';
import ReportsListRowActions from '../components/ReportsListRowActions';
import ReportsPageHeader from '../components/ReportsPageHeader';
import ReportsSurface from '../components/ReportsSurface';
import { useReportsPermissions } from '../hooks/useReportsPermissions.hook';
import { formatReportTimestamp } from '../utils/reportDisplay.util';

const ALL = '';

/**
 * Report list with customer/status filters and links to view and edit.
 */
const ReportsListPage = () => {
  const toast = useToast();
  const { canEdit } = useReportsPermissions();
  const [customerFilter, setCustomerFilter] = useState(ALL);
  const [statusFilter, setStatusFilter] = useState('active');
  const [periodQuery, setPeriodQuery] = useState('');

  const [customers, customersLoading, loadCustomers] = useApiCall(
    reportsRequests.listCustomers,
  );
  const [reportsResponse, reportsLoading, loadReports] = useApiCall(
    reportsRequests.listReports,
  );
  const [reports, setReports] = useState<ReportDto[] | null>(null);
  const [archiveLoading, setArchiveLoading] = useState(false);

  useEffect(() => {
    loadCustomers({ params: null });
  }, [loadCustomers]);

  useEffect(() => {
    loadReports({
      params: customerFilter ? { customerId: customerFilter } : {},
    });
  }, [loadReports, customerFilter]);

  useEffect(() => {
    if (reportsResponse) {
      setReports(reportsResponse);
    }
  }, [reportsResponse]);

  const customerMap = useMemo(() => {
    const map = new Map<string, ReportCustomerDto>();
    (customers ?? []).forEach(customer => map.set(customer.id, customer));
    return map;
  }, [customers]);

  const filteredReports = useMemo(() => {
    return (reports ?? []).filter(report => {
      if (statusFilter && report.status !== statusFilter) {
        return false;
      }
      if (
        periodQuery &&
        !report.name.toLowerCase().includes(periodQuery.toLowerCase()) &&
        !formatWarehouseMeasurementPeriodLabel(
          report.measurementPeriod ?? report.reportingPeriod ?? '',
        )
          .toLowerCase()
          .includes(periodQuery.toLowerCase()) &&
        !(report.measurementPeriod ?? report.reportingPeriod ?? '')
          .toLowerCase()
          .includes(periodQuery.toLowerCase())
      ) {
        return false;
      }
      return true;
    });
  }, [reports, statusFilter, periodQuery]);

  const handleArchiveToggle = (report: ReportDto) => {
    const isArchived = report.status === 'archived';
    const archiveRequest = isArchived
      ? reportsRequests.unarchiveReport
      : reportsRequests.archiveReport;

    setArchiveLoading(true);
    void makeRequest(archiveRequest, { params: { id: report.id } })
      .then(updatedReport => {
        setReports(prev =>
          (prev ?? []).map(row =>
            row.id === updatedReport.id
              ? {
                  ...row,
                  status: updatedReport.status,
                  updatedAt: updatedReport.updatedAt,
                }
              : row,
          ),
        );
        toast(
          isArchived ? 'Report restored' : 'Report archived',
          'success',
        );
      })
      .catch(() => {
        toast('Could not update report status', 'error');
      })
      .finally(() => {
        setArchiveLoading(false);
      });
  };

  if (customersLoading || reportsLoading) {
    return <LoadingPage />;
  }

  return (
    <FlexContainer
      direction="column"
      gap={2}
      width="100%"
      height="auto"
      overflowAllVisible={true}
    >
      <ReportsPageHeader
        title="Reports"
        subtitle="Monthly, quarterly, and Joint Operating Committee (JOC) customer reports from the 18-section library."
        action={
          canEdit ? (
            <LinkButton to={{ pathname: '/new' }} trackingName="reports-list-new">
              New report
            </LinkButton>
          ) : null
        }
      />

      <ReportsSurface>
        <FlexContainer
          direction="row"
          gap={2}
          flexWrap={true}
          width="100%"
          height="auto"
          overflowAllVisible={true}
        >
          <FilterField label="Customer">
            <Select
              size="small"
              fullWidth
              displayEmpty
              value={customerFilter}
              onChange={event => setCustomerFilter(event.target.value)}
            >
              <MenuItem value={ALL}>All customers</MenuItem>
              {(customers ?? []).map(customer => (
                <MenuItem key={customer.id} value={customer.id}>
                  {customer.name}
                </MenuItem>
              ))}
            </Select>
          </FilterField>
          <FilterField label="Status">
            <Select
              size="small"
              fullWidth
              displayEmpty
              value={statusFilter}
              onChange={event => setStatusFilter(event.target.value)}
            >
              <MenuItem value={ALL}>All statuses</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="archived">Archived</MenuItem>
            </Select>
          </FilterField>
          <FilterField label="Search name or period">
            <TextField
              size="small"
              fullWidth
              value={periodQuery}
              onChange={event => setPeriodQuery(event.target.value)}
              placeholder="e.g. Acme Health or Apr 2026"
            />
          </FilterField>
        </FlexContainer>
      </ReportsSurface>

      {filteredReports.length === 0 ? (
        <ReportsSurface padding={4}>
          <FlexContainer
            direction="column"
            alignItems="center"
            gap={1.5}
            width="100%"
            height="auto"
            overflowAllVisible={true}
          >
            <Typography color="text.secondary" align="center">
              No reports match these filters.
            </Typography>
            {canEdit ? (
              <LinkButton
                to={{ pathname: '/new' }}
                trackingName="reports-list-first"
              >
                Create your first report
              </LinkButton>
            ) : null}
          </FlexContainer>
        </ReportsSurface>
      ) : (
        <ReportsSurface padding={0}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Measurement period</TableCell>
                <TableCell>Updated</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredReports.map(report => (
                <TableRow key={report.id} hover>
                  <TableCell>
                    <Link to={`/${report.id}/view`}>{report.name}</Link>
                  </TableCell>
                  <TableCell>
                    {customerMap.get(report.customerId)?.name ??
                      report.customerId}
                  </TableCell>
                  <TableCell>
                    {formatWarehouseMeasurementPeriodLabel(
                      report.measurementPeriod ?? report.reportingPeriod ?? '',
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatReportTimestamp(report.updatedAt)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <ReportsListRowActions
                      report={report}
                      canEdit={canEdit}
                      archiveLoading={archiveLoading}
                      onArchiveToggle={handleArchiveToggle}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ReportsSurface>
      )}
    </FlexContainer>
  );
};

interface FilterFieldProps {
  label: string;
  children: ReactNode;
}

const FilterField = ({ label, children }: FilterFieldProps) => (
  <FlexContainer
    direction="column"
    gap={0.5}
    minWidth="200px"
    flexGrow={1}
    height="auto"
    overflowAllVisible={true}
  >
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    {children}
  </FlexContainer>
);

export default ReportsListPage;
